import { Session } from 'sip.js';
import {
  TerminationReason,
  CallTerminationEvent,
  TerminationDetails,
  CreateEventParams,
  ExtractedTerminationData,
  SessionWithInternals,
} from './index.types';

export class VoipTerminationService {
  private static instance: VoipTerminationService;

  private constructor() {}

  static getInstance(): VoipTerminationService {
    if (!VoipTerminationService.instance) {
      VoipTerminationService.instance = new VoipTerminationService();
    }
    return VoipTerminationService.instance;
  }

  extractTerminationDetails(
    session: Session,
    isUserInitiated = false
  ): ExtractedTerminationData {
    const result: ExtractedTerminationData = {};
    const sessionWithInternals = session as SessionWithInternals;

    if (sessionWithInternals._dialog?.remoteTarget && !isUserInitiated) {
      result.reason = TerminationReason.REMOTE_BYE;
    }

    const terminationDetails = sessionWithInternals._terminationDetails;
    if (!terminationDetails) {
      return result;
    }

    const { statusCode, reasonPhrase, cause } = terminationDetails;

    if (!isUserInitiated) {
      result.statusCode = statusCode;
      result.reasonPhrase = reasonPhrase;
      result.sipjsCause = cause;
    }

    result.additionalDetails = {
      statusCode,
      reasonPhrase,
      cause,
      ...terminationDetails,
    };

    return result;
  }

  private evaluateSipCode(
    statusCode: number,
    reasonPhrase?: string
  ): TerminationReason {
    switch (statusCode) {
      case 200:
        return TerminationReason.REMOTE_BYE;
      case 408:
      case 504:
        return TerminationReason.NETWORK_TIMEOUT;
      case 480:
      case 486:
      case 600:
        return TerminationReason.NO_ANSWER;
      case 487:
        if (reasonPhrase?.toLowerCase().includes('cancel')) {
          return TerminationReason.CANCEL;
        }
        return TerminationReason.REMOTE_BYE;
      case 400:
      case 401:
      case 403:
      case 404:
      case 405:
      case 406:
      case 407:
      case 488:
      case 500:
      case 502:
      case 503:
      default:
        return statusCode >= 400
          ? TerminationReason.SIP_ERROR
          : TerminationReason.UNKNOWN;
    }
  }

  mapTerminationReason(
    statusCode?: number,
    reasonPhrase?: string,
    sessionState?: string,
    userInitiated?: boolean,
    isTimeout?: boolean
  ): TerminationReason {
    if (isTimeout) return TerminationReason.NETWORK_TIMEOUT;

    if (statusCode === 408 || statusCode === 504) {
      return TerminationReason.NETWORK_TIMEOUT;
    }

    if (userInitiated) {
      return TerminationReason.USER_HANGUP;
    }

    if (statusCode) {
      return this.evaluateSipCode(statusCode, reasonPhrase);
    }

    if (sessionState === 'Terminated') {
      return TerminationReason.REMOTE_BYE;
    }

    return TerminationReason.UNKNOWN;
  }

  createEvent(params: CreateEventParams): CallTerminationEvent {
    const endTime = new Date();
    const now = endTime.toISOString();

    let duration = params.callDurationSeconds;
    if (duration === undefined && params.callStartTime) {
      const startTime = new Date(params.callStartTime);
      duration = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
    } else if (duration === undefined) {
      duration = 0;
    }

    let terminationDetails: TerminationDetails | undefined;

    if (
      params.sipStatusCode ||
      params.sipReasonPhrase ||
      params.sipjsCause ||
      params.additionalTerminationDetails
    ) {
      terminationDetails = {
        ...(params.additionalTerminationDetails || {}),
        ...(params.sipStatusCode && { sipStatusCode: params.sipStatusCode }),
        ...(params.sipReasonPhrase && {
          sipReasonPhrase: params.sipReasonPhrase,
        }),
        ...(params.sipjsCause && { sipjsCause: params.sipjsCause }),
      };
    }

    return {
      eventName: 'CallTerminationEvent',
      timestamp: now,
      platform: 'web',
      personId: params.personId,
      callId: params.callId,
      caller: params.caller,
      callee: params.callee,
      callDurationSeconds: duration,
      terminationReason: params.terminationReason,
      terminationDetails,
    };
  }
}
